/**
 * YetkiContext — kullanıcının ETKİN yetkilerini (rol matrisi + kişisel override)
 * backend'den çeker ve uygulamaya can()/sayfaGorebilir() ile sunar.
 *
 * Dayanıklı tasarım: yetki henüz yüklenmediyse veya endpoint yoksa (eski sürüm),
 * `yuklendi=false` döner; tüketiciler (PanelSidebar) bu durumda eski statik
 * ROL_YETKILERI'ne düşer → hiçbir şey bozulmaz.
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const YetkiContext = createContext();

// ⚡ Menü hızı: son bilinen yetkiyi localStorage'da tut → sonraki açılışta menü ANINDA
// (statik→dinamik flicker'ı önler). Cache KULLANICI-BAZLI (uid) → cross-user sızıntı YOK.
// Güvenlik: backend her istekte yetkiyi zaten enforce eder; stale menü yalnız kozmetik ve
// fetchYetkiler ~150ms içinde düzeltir.
const YETKI_CACHE_PREFIX = 'yetki_cache_';

const _yetkiCacheOku = (uid) => {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(YETKI_CACHE_PREFIX + uid);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && p.yetkiler && Array.isArray(p.gorunur_sayfalar)) return p;
  } catch { /* noop */ }
  return null;
};

const _yetkiCacheYaz = (uid, yetkiler, gorunur_sayfalar) => {
  if (!uid) return;
  try {
    localStorage.setItem(YETKI_CACHE_PREFIX + uid, JSON.stringify({ yetkiler, gorunur_sayfalar }));
  } catch { /* noop */ }
};

export const useYetki = () => {
  const ctx = useContext(YetkiContext);
  if (!ctx) {
    // Provider dışında çağrılırsa güvenli varsayılan
    return {
      yetkiler: null, gorunurSayfalar: null, yuklendi: false, loading: false,
      can: () => null, sayfaGorebilir: () => null, refresh: () => {},
    };
  }
  return ctx;
};

export const YetkiProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [yetkiler, setYetkiler] = useState(null);          // { sayfa: { aksiyon: bool } }
  const [gorunurSayfalar, setGorunurSayfalar] = useState(null); // [sayfa, ...]
  const [yuklendi, setYuklendi] = useState(false);
  const [loading, setLoading] = useState(false);
  const sonKullanici = useRef(null);

  const fetchYetkiler = useCallback(async () => {
    const tkn = token || localStorage.getItem('token');
    if (!tkn || !user) {
      setYetkiler(null); setGorunurSayfalar(null); setYuklendi(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/panel/yetki/etkin`, {
        headers: { Authorization: `Bearer ${tkn}` },
      });
      if (res.data?.success) {
        const y = res.data.yetkiler || {};
        const g = res.data.gorunur_sayfalar || [];
        setYetkiler(y);
        setGorunurSayfalar(g);
        setYuklendi(true);
        // 💾 Sonraki açılışta anında hydrate için cache'le (kullanıcı-bazlı)
        _yetkiCacheYaz(user?.id || user?.user_id || user?.email || null, y, g);
      } else {
        setYuklendi(false);
      }
    } catch (e) {
      // Ağ/endpoint hatası → GÜVENLİ tarafta kal: yuklendi=false → statik ROL_YETKILERI
      // fallback (orijinal davranış). Cache'ten hydrate edilmiş STALE yetkiyi ağ kesintisinde
      // sonsuza dek göstermeyiz (yeni revoke'lar görünmez kalmasın). Hydrate faydası zaten
      // başarılı fetch yolunda (yaygın durum) korunur.
      setYuklendi(false);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    const uid = user?.id || user?.user_id || user?.email || null;
    if (uid !== sonKullanici.current) {
      sonKullanici.current = uid;
      // ⚡ ANINDA hydrate: son bilinen yetkiyi cache'ten göster (menü flicker'ı önle),
      //    sonra arka planda tazele. Farklı/yeni kullanıcıda önceki state temizlenir.
      if (uid) {
        const cached = _yetkiCacheOku(uid);
        if (cached) {
          setYetkiler(cached.yetkiler);
          setGorunurSayfalar(cached.gorunur_sayfalar);
          setYuklendi(true);
        } else {
          setYetkiler(null); setGorunurSayfalar(null); setYuklendi(false);
        }
      } else {
        setYetkiler(null); setGorunurSayfalar(null); setYuklendi(false);
      }
      fetchYetkiler();
    }
  }, [user, fetchYetkiler]);

  // can(sayfa, aksiyon): yüklüyse bool, değilse null (bilinmiyor)
  const can = useCallback((sayfa, aksiyon = 'goruntule') => {
    if (!yuklendi || !yetkiler) return null;
    if (!yetkiler[sayfa]) return null;   // katalogda yok → bilinmiyor (statik listeye düş)
    return !!yetkiler[sayfa][aksiyon];
  }, [yuklendi, yetkiler]);

  // sayfaGorebilir(sayfa): yüklüyse bool, değilse null
  // ⚠️ Frontend'e yeni sayfa eklenip backend yetki kataloğuna (yetki_config.PAGE_CATALOG)
  // eklenmezse, o sayfa `yetkiler` matrisinde HİÇ olmaz. Bunu "yasak" saymak menüyü
  // herkesten gizler; doğrusu "bilinmiyor" deyip statik ROL_YETKILERI'ne düşmektir.
  const sayfaGorebilir = useCallback((sayfa) => {
    if (!yuklendi || !gorunurSayfalar) return null;
    if (yetkiler && !yetkiler[sayfa]) return null;   // katalog dışı → karar verme
    return gorunurSayfalar.includes(sayfa);
  }, [yuklendi, gorunurSayfalar, yetkiler]);

  return (
    <YetkiContext.Provider value={{
      yetkiler, gorunurSayfalar, yuklendi, loading,
      can, sayfaGorebilir, refresh: fetchYetkiler,
    }}>
      {children}
    </YetkiContext.Provider>
  );
};

export default YetkiContext;
